import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { Facebook, Plus, Loader2, Unplug, KeyRound, ExternalLink, RefreshCw, Users, ThumbsUp, Star, BadgeCheck, Globe, Phone, MapPin } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Loading, ErrorState, EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import {
  useZaloAccounts,
  useConnectFacebookPage,
  useDeleteZaloAccount,
  useFacebookConfig,
  useFbTokenImportStatus,
  useFbPageInfo,
  statusMeta,
  type ChannelAccount,
  type FbPageInfo,
  PLATFORM,
  type FbConfigStatus,
} from '@/hooks/use-integrations'
import { ChannelCard } from './channel-card'
import { ChannelSetupGuide } from './channel-setup-guide'
import { FbTokenImportDialog } from './fb-token-import-dialog'

/** Hiện khi backend chưa có khoá ứng dụng Meta. */
function FbSetupGuide({ cfg }: { cfg: FbConfigStatus }) {
  return (
    <ChannelSetupGuide
      title="Chưa cấu hình ứng dụng Facebook"
      intro={`Máy chủ đã sẵn sàng: đổi token ngắn hạn sang dài hạn (~60 ngày), tự đăng ký webhook cho từng Fanpage, kiểm chữ ký HMAC. Đang dùng Graph API ${cfg.graphVersion}. Chỉ còn thiếu khoá ứng dụng.`}
      steps={[
        <>
          Tạo ứng dụng loại <strong>Business</strong> tại{' '}
          <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="font-medium text-primary underline">
            developers.facebook.com/apps
          </a>{' '}
          rồi thêm sản phẩm <strong>Messenger</strong>.
        </>,
        <>
          Trong <strong>Facebook Login → Settings</strong>, dán Callback URL bên dưới vào ô
          <em> Valid OAuth Redirect URIs</em>.
        </>,
        <>
          Trong <strong>Messenger → Webhooks</strong>, dán Webhook URL và Verify Token bên dưới,
          rồi đăng ký các trường: <code className="rounded bg-background px-1">{cfg.webhookFields.join(', ')}</code>.
        </>,
        <>
          Xin duyệt các quyền: <code className="rounded bg-background px-1">{cfg.scopes.join(', ')}</code>.
          Chưa duyệt thì chỉ tài khoản có vai trò trong ứng dụng mới kết nối được.
        </>,
        <>
          Lấy <strong>App ID</strong> và <strong>App Secret</strong>, đặt vào{' '}
          <code className="rounded bg-background px-1">backend/.env</code> rồi khởi động lại backend.
        </>,
      ]}
      copyItems={[
        { label: 'Valid OAuth Redirect URI', value: cfg.redirectUri },
        { label: 'Webhook URL (Callback URL)', value: cfg.webhookUrl },
        ...(cfg.suggestedVerifyToken
          ? [{ label: 'Verify Token — tự đặt, dán giống nhau ở cả .env và Meta', value: cfg.suggestedVerifyToken }]
          : []),
      ]}
      missing={cfg.missing.map((k) =>
        k === 'FACEBOOK_WEBHOOK_VERIFY_TOKEN' && cfg.suggestedVerifyToken
          ? `${k}=${cfg.suggestedVerifyToken}`
          : `${k}=`,
      )}
      footnote={
        <>
          Meta bắt buộc Webhook URL phải là <strong>HTTPS công khai</strong> và tự gọi thử ngay khi
          lưu — chạy máy cục bộ phải mở đường hầm trước, rồi đặt{' '}
          <code className="rounded bg-background px-1">PUBLIC_API_URL</code> trỏ tới đó.
          Khác Zalo OA, webhook Facebook <strong>luôn bắt buộc chữ ký</strong>: sai chữ ký trả 401,
          không có chế độ nới lỏng.
        </>
      }
    />
  )
}

export function FacebookPageTab() {
  // Không có endpoint riêng cho Facebook — dùng danh sách kênh tổng rồi lọc theo platform.
  const { data, isLoading, isError } = useZaloAccounts()
  const { data: cfg } = useFacebookConfig()
  const connect = useConnectFacebookPage()
  const remove = useDeleteZaloAccount()
  // Thêm page thủ công bằng token — backend bật/tắt qua FACEBOOK_TOKEN_IMPORT_ENABLED.
  const { data: tokenImport } = useFbTokenImportStatus()
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false)

  const pages = useMemo(
    () => (data ?? []).filter((a) => a.platform === PLATFORM.FACEBOOK_PAGE),
    [data],
  )

  const handleConnect = () => {
    connect.mutate(undefined, {
      onSuccess: (url) => {
        window.location.href = url
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  const handleDisconnect = (id: string) => {
    remove.mutate(id, {
      onSuccess: () => toast.success('Đã ngắt kết nối Fanpage'),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Kết nối Fanpage để nhận và trả lời tin nhắn Messenger ngay trong hệ thống.
        </p>
        <div className="flex shrink-0 gap-2">
          {tokenImport?.enabled && (
            <Button variant="outline" onClick={() => setTokenDialogOpen(true)} title="Dán User Access Token để chọn page cần thêm">
              <KeyRound /> Thêm bằng token
            </Button>
          )}
          <Button onClick={handleConnect} disabled={connect.isPending || cfg?.configured === false}>
            {connect.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
            Kết nối Fanpage
          </Button>
        </div>
      </div>
      {tokenImport?.enabled && (
        <FbTokenImportDialog
          open={tokenDialogOpen}
          onOpenChange={setTokenDialogOpen}
          daKetNoi={new Set(pages.map((a) => a.externalPageId).filter((x): x is string => !!x))}
        />
      )}

      {cfg && !cfg.configured && <FbSetupGuide cfg={cfg} />}

      {isLoading ? (
        <Loading label="Đang tải Fanpage..." />
      ) : isError ? (
        <ErrorState />
      ) : !pages.length ? (
        <EmptyState
          icon={Facebook}
          title="Chưa kết nối Fanpage nào"
          description="Nhấn Kết nối Fanpage và chọn các trang bạn quản lý trên Facebook."
        />
      ) : (
        <div className="space-y-3">
          {pages.map((acc) => (
            <ChannelCard
              key={acc.id}
              icon={<Facebook className="h-5 w-5" />}
              avatarUrl={acc.avatarUrl}
              title={acc.displayName || 'Facebook Page'}
              subtitle={
                <>
                  {acc.externalPageId ? `Page ID ${acc.externalPageId} · ` : ''}
                  {acc.lastConnectedAt
                    ? `Kết nối lần cuối ${dayjs(acc.lastConnectedAt).format('DD/MM/YYYY HH:mm')}`
                    : 'Chưa từng kết nối'}
                </>
              }
              status={statusMeta(acc.liveStatus, acc.isDisabled)}
              actions={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDisconnect(acc.id)}
                  disabled={remove.isPending}
                  title="Ngắt kết nối"
                >
                  <Unplug />
                </Button>
              }
            >
              <ThongTinPage acc={acc} />
            </ChannelCard>
          ))}
        </div>
      )}
    </div>
  )
}


const soVN = (n?: number) => (typeof n === 'number' ? new Intl.NumberFormat('vi-VN').format(n) : '—')

/** Khối thông tin công khai của page: link, follower, danh mục… Tự lấy lần đầu nếu chưa có cache. */
function ThongTinPage({ acc }: { acc: ChannelAccount }) {
  const layInfo = useFbPageInfo()
  const info: FbPageInfo | null | undefined = acc.channelInfo
  // Chưa từng lấy → gọi một lần (máy chủ cache 6 giờ).
  useEffect(() => {
    if (!info && !layInfo.isPending && !layInfo.isError && acc.liveStatus === 'connected') layInfo.mutate({ id: acc.id })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acc.id, !!info])

  if (!info) {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        {layInfo.isPending ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Đang lấy thông tin page…</> : 'Chưa có thông tin page.'}
        {!layInfo.isPending && (
          <button type="button" className="text-primary hover:underline" onClick={() => layInfo.mutate({ id: acc.id, refresh: true })}>Lấy ngay</button>
        )}
      </p>
    )
  }

  const chiSo = [
    { icon: Users, nhan: 'Người theo dõi', so: soVN(info.followersCount) },
    { icon: ThumbsUp, nhan: 'Lượt thích', so: soVN(info.fanCount) },
    { icon: Star, nhan: 'Đánh giá', so: info.ratingCount ? `${info.overallStarRating ?? '—'}★ (${soVN(info.ratingCount)})` : '—' },
  ]

  return (
    <div className="space-y-2 text-xs">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {chiSo.map((c) => (
          <span key={c.nhan} className="inline-flex items-center gap-1 text-muted-foreground" title={c.nhan}>
            <c.icon className="h-3.5 w-3.5" /> <span className="font-medium text-foreground tabular-nums">{c.so}</span> {c.nhan.toLowerCase()}
          </span>
        ))}
        {info.verificationStatus && info.verificationStatus !== 'not_verified' && (
          <span className="inline-flex items-center gap-1 text-primary" title={`Xác minh: ${info.verificationStatus}`}><BadgeCheck className="h-3.5 w-3.5" /> Đã xác minh</span>
        )}
        {info.isPublished === false && <span className="rounded bg-warning/15 px-1.5 py-0.5 text-warning">Page chưa công khai</span>}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground">
        {info.link && (
          <a href={info.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
            <ExternalLink className="h-3.5 w-3.5" /> {info.username ? `facebook.com/${info.username}` : 'Mở trang Facebook'}
          </a>
        )}
        {(info.category || info.categories?.length) && <span>{[info.category, ...(info.categories ?? []).filter((c) => c !== info.category)].filter(Boolean).slice(0, 3).join(' · ')}</span>}
        {info.website && <a href={/^https?:/.test(info.website) ? info.website : `https://${info.website}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline"><Globe className="h-3.5 w-3.5" /> {info.website.replace(/^https?:\/\//, '')}</a>}
        {info.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {info.phone}</span>}
        {info.address && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {info.address}</span>}
      </div>
      {info.about && <p className="line-clamp-2 text-muted-foreground" title={info.about}>{info.about}</p>}
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
        {info.fetchedAt && <span>Cập nhật {dayjs(info.fetchedAt).format('DD/MM HH:mm')}</span>}
        {info.error && <span className="text-warning" title={info.error}>· không làm mới được (dữ liệu cũ)</span>}
        <button
          type="button"
          className="inline-flex items-center gap-1 text-primary hover:underline disabled:opacity-50"
          disabled={layInfo.isPending}
          onClick={() => layInfo.mutate({ id: acc.id, refresh: true }, { onError: (e) => toast.error(apiError(e)) })}
        >
          {layInfo.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Làm mới
        </button>
      </div>
    </div>
  )
}
